import { Form, Formik } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import Confirm from '../../../components/confirm';
import ConfirmSelect from '../../../components/confirm-select';
import SaveBackdrop from '../../../components/saveBackdrop';
import Wait from '../../../components/wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  apiReponseError, apiRequestError, canReadKit, canWriteKit, compareArrayObjectValue,
  formatDateForDataGrid, generateQRCode, getErrorObject, getKitItemDetail, isFormDataValid
} from '../../../utils/helper';
import { DeleteIcon, GetAppToneIcon, SearchIcon } from '../../../utils/icons';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, FormGroup, FormHelperText, FormLabel, Grid, IconButton, Input, InputAdornment,
  InputLabel, Link, Select, Switch, TextField, Typography
} from '../../../utils/material-ui-core';

interface kititemCRUDProps {
  open: boolean;
  kitKey: string;
  kitItemKey: string;
  isSubscriptionBilling: boolean;
  onCallback: (confirmOutput: ConfirmOutput) => void;
}

// Default form value
const formDefaultValue = {
  deviceId: 0,
  deviceOwnership: "1",
  deviceOwnershipKey: "",
  fulfillable: 0,
  intervalFrequency: 0,
  intervalUnit: "",
  kitItemKey: "",
  packageKitRuleId: 0,
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  name: "",
  partNumber: "",
  platformVendorKey: "",
  platformVendorName: "",
  price: 0,
  quantity: 1,
  rechargeProductStatusDescription: "",
  rechargeProductStatusId: 0,
  rollupItem: false,
  sequence: 0,
  shopifyVariantId: "",
  subscriptionBilling: false,
  subscriptionId: 0,
  title: "",
  taxable: false,
};

const formDefaultTouchValue = {
  deviceId: false,
  deviceOwnership: false,
  deviceOwnershipKey: false,
  fulfillable: false,
  intervalFrequency: false,
  intervalUnit: false,
  kitItemKey: false,
  lastModifiedBy: false,
  lastModifiedDateTime: false,
  name: false,
  partNumber: false,
  platformVendorKey: false,
  platformVendorName: false,
  price: false,
  quantity: false,
  rechargeProductStatusDescription: false,
  rechargeProductStatusId: false,
  rollupItem: false,
  sequence: false,
  shopifyVariantId: false,
  subscriptionBilling: false,
  subscriptionId: false,
  title: false,
  taxable: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  partNumber: Yup.string().required("SKU is required."),
  deviceOwnershipKey: Yup.string().required("Ownership is required."),
  platformVendorKey: Yup.string().required("Platform vendor is required"),
  quantity: Yup.number().moreThan(0, "Quantity must be greater than zero.").required("Quantity is required"),
});

const KitItemCRUD = ({ open, kitKey, kitItemKey, isSubscriptionBilling, onCallback }: kititemCRUDProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { referenceLookups, userFunctionAccess, showAlert } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [loading, setLoading] = useState<boolean>(true);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(true);
  const [qrcodeBase64String, setQrcodeBase64String] = useState<string>("");
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [initialFormValues, setInitialFormValues] = useState<KitItem>(formDefaultValue);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);
  const [addKitItemConfirmOpen, setAddKitItemConfirmOpen] = useState<boolean>(false);
  const [newKitItemLookupValues, setNewKitItemLookupValues] = useState<Lookup[]>([]);
  const [rechargeProductSearchInProgress, setRechargeProductSearchInProgress] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  const setQRCode = async (text: string) => {
    const apiResponse2 = await generateQRCode(text);
    if (apiResponse2.success) {
      const data2 = apiResponse2.data;
      setQrcodeBase64String(data2.qrcodeBase64String);
    } else {
      setQrcodeBase64String("");
    }
  };

  const getRechargeProductDetail = async (sku: string, rollupItem: boolean) => {
    try {
      if (!rollupItem) {
        return;
      }

      if (sku === "" || sku === null || sku === undefined) {
        return;
      }

      setRechargeProductSearchInProgress(true);

      formReference.current.setFieldValue("shopifyVariantId", "");
      formReference.current.setFieldValue("subscriptionId", 0);
      formReference.current.setFieldValue("title", "");
      formReference.current.setFieldValue("price", 0);
      formReference.current.setFieldValue("intervalFrequency", 0);
      formReference.current.setFieldValue("intervalUnit", "");
      formReference.current.setFieldValue("taxable", false);

      await API.get("/api/shopify/recharge/products/" + sku)
        .then(async (response) => {
          if (response.data.rechargeProduct != null) {
            formReference.current.setFieldValue("shopifyVariantId", response.data.rechargeProduct.shopifyProductId);
            formReference.current.setFieldValue("subscriptionId", response.data.rechargeProduct.id);
            formReference.current.setFieldValue("title", response.data.rechargeProduct.title);
            formReference.current.setFieldValue("price", response.data.rechargeProduct.price);
            formReference.current.setFieldValue("intervalFrequency", response.data.rechargeProduct.chargeIntervalFrequency);
            formReference.current.setFieldValue("intervalUnit", response.data.rechargeProduct.orderIntervalUnit);
            formReference.current.setFieldValue("taxable", response.data.rechargeProduct.taxable);
          } else {
            formReference.current.setFieldValue("shopifyVariantId", "");
          }

          setRechargeProductSearchInProgress(false);
        })
        .catch((error) => {
          showAlert({
            message: apiReponseError(error, "Failed to get recharge product details."),
            severity: "error",
          });
          setRechargeProductSearchInProgress(false);
        });
    } catch (e) {
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
      setRechargeProductSearchInProgress(false);
    }
  };
  const saveKitItem = async () => {
    if (!canWriteKit(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      cancel();
      return;
    }

    const payload = {
      kitItem: {
        partNumber: formReference.current.values.partNumber,
        kitItemKey: formReference.current.values.kitItemKey,
        deviceOwnershipKey: formReference.current.values.deviceOwnershipKey,
        packageKitRuleId: formReference.current.values.packageKitRuleId,
        rollupItem: formReference.current.values.rollupItem,
        quantity: formReference.current.values.quantity,
      },
    };

    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/kits/" + kitKey + "/kititems/" + formReference.current.values.kitItemKey, payload)
        .then(async (response) => {
          showAlert({ message: "Successfully saved.", severity: "success" });
          await onCallback({ status: "refresh", data: "" });
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "kitItem.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save kit item data."),
              severity: "error",
            });
          }
        });
    } catch (e) {
      setSaveInProgress(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmDialogOpen(true);
  };

  const deleteKitItem = async (confirm: boolean) => {
    setDeleteConfirmDialogOpen(false);
    if (confirm) {
      try {
        await API.delete("/api/onboarding/kits/" + kitKey + "/kitItems/" + kitItemKey)
          .then(async (response) => {
            showAlert({
              message: "Successfully removed.",
              severity: "success",
            });
            await onCallback({ status: "refresh", data: "" });
            return;
          })
          .catch((error) => {
            showAlert({
              message: apiReponseError(error, "Failed to delete kit item data."),
              severity: "error",
            });
          });
      } catch (e) {
        showAlert({
          message: apiRequestError(e, "Something went wrong in our system."),
          severity: "error",
        });
      }
    }
  };

  const cancelKitItem = () => {
    formReference.current.resetForm({});
    onCallback({ status: "cancel", data: "" });
  };

  const selectNewKitItem = (confirmOutput: ConfirmOutput) => {
    setAddKitItemConfirmOpen(false);

    if (confirmOutput.status === "select") {
      const filterSKU = referenceLookups.skus.filter((sku) => sku.skuKey === confirmOutput.data.value);
      if (filterSKU.length > 0) {
        formReference.current.setFieldValue("partNumber", filterSKU[0].partNumber);
        formReference.current.setFieldValue("name", filterSKU[0].name);
        formReference.current.setFieldValue("platformVendorKey", filterSKU[0].vendorKey);
        formReference.current.setFieldValue("platformVendorName", filterSKU[0].vendorName);
        setSaveButtonVisible(true);
        setQRCode(filterSKU[0].partNumber);
        getRechargeProductDetail(filterSKU[0].partNumber, formReference.current.values.rollupItem);
      }
    }
  };

  const handleAddNewKit = (mode: number) => {
    if (readonly && formReference.current.kitItemKey === "") {
      return;
    }

    const lookupValues: Lookup[] = [];
    const skus = referenceLookups.skus;

    for (let i = 0; i < skus.length; i++) {
      if (mode === 1) {
        lookupValues.push({
          value: skus[i].skuKey,
          name: skus[i].name + " - " + skus[i].partNumber,
          checked: false,
        });
      } else {
        lookupValues.push({
          value: skus[i].skuKey,
          name: skus[i].partNumber + " - " + skus[i].name,
          checked: false,
        });
      }
    }

    setNewKitItemLookupValues(lookupValues.sort(compareArrayObjectValue("name", "asc")));
    setAddKitItemConfirmOpen(true);
  };

  const cancel = () => {
    onCallback({ status: "cancel", data: "" });
  };

  useEffect(() => {
    const initialPageAsync = async () => {
      if (open) {
        setReadonly(true);
        setLoading(true);
        setSaveButtonVisible(false);

        if (isFormDataValid(formReference)) {
          formReference.current.resetForm({});
        }

        if (!canReadKit(userFunctionAccess)) {
          showAlert({ message: "Access Denied.", severity: "error" });
          cancel();
          return;
        }

        if (kitItemKey === "") {
          if (!canWriteKit(userFunctionAccess)) {
            showAlert({ message: "Access Denied.", severity: "error" });
            cancel();
            return;
          }

          let defaultDeviceOwnershipKey = "";
          const filterDeviceOwnership = referenceLookups.deviceOwnerships.filter((ownership) => ownership.name === "Client Company");
          if (filterDeviceOwnership.length === 1) {
            defaultDeviceOwnershipKey = filterDeviceOwnership[0].deviceOwnershipKey;
          }

          setInitialFormValues({
            ...formDefaultValue,
            kitItemKey: referenceLookups.newRecordDefaultId,
            deviceOwnershipKey: defaultDeviceOwnershipKey,
          } as KitItem);
          setIsNew(true);
          setReadonly(false);
          setLoading(false);
          setTimeout(() => {
            handleAddNewKit(0);
          }, 200);
        } else {
          const apiResponse = await getKitItemDetail(kitKey, kitItemKey);
          if (apiResponse.success) {
            const data = apiResponse.data;
            setInitialFormValues(data.kitItem as KitItem);
            setIsNew(false);
            setReadonly(!canWriteKit(userFunctionAccess));
            setQRCode(data.kitItem.partNumber);
            setLoading(false);
          } else {
            showAlert({ message: apiResponse.errorMessage, severity: "error" });
            cancel();
            return;
          }
        }
      }
    };
    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, referenceLookups.newRecordDefaultId, kitItemKey, kitKey]);

  return (
    <>
      {formReference.current !== null ? <Confirm title="Confirmation" content={`<p>Are you sure you want to delete this item?</p><p style="text-align:center"><b>${formReference.current.values.name} (${formReference.current.values.partNumber})</b></p><p style="text-align:center"><b>Total Quantity : ${formReference.current.values.quantity} </b></p>`} yesText="DELETE" noText="CANCEL" open={deleteConfirmDialogOpen} callbackOnClose={() => setDeleteConfirmDialogOpen} callbackOnConfirm={deleteKitItem} /> : null}

      <ConfirmSelect title="Select SKU" label={"Kit"} content={``} data={newKitItemLookupValues} yesText="SELECT" noText="CANCEL" open={addKitItemConfirmOpen} multiSelect={false} maxSelect={1} callbackOnSelect={selectNewKitItem} />

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"sm"} fullWidth={true}>
        <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            {readonly ? "View Kit Item" : isNew ? "New Kit Item" : "Edit Kit Item"}
          </Typography>
        </DialogTitle>
        {loading ? (
          <>
            <DialogContent dividers>
              <Wait />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => cancel()} color="secondary" autoFocus>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent dividers className={globalStyles.formikContainer}>
              <SaveBackdrop open={saveInProgress} />

              <Box className={pageStyles.contentText}>
                <Formik
                  innerRef={formReference}
                  initialValues={initialFormValues}
                  validationSchema={formValidationSchema}
                  validateOnMount={true}
                  initialTouched={formDefaultTouchValue}
                  validateOnBlur={true}
                  validateOnChange={true}
                  onSubmit={(values: KitItem) => {
                    saveKitItem();
                  }}
                >
                  {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue, resetForm }) => (
                    <Form
                      onChange={() => {
                        setSaveButtonVisible(true);
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={6} lg={6}>
                          <FormControl fullWidth>
                            <InputLabel>SKU</InputLabel>
                            <Input
                              required
                              disabled={true}
                              id="partNumber"
                              value={values.partNumber}
                              onChange={handleChange}
                              onClick={() => {
                                if (!readonly && isNew) {
                                  handleAddNewKit(0);
                                }
                              }}
                              error={touched.partNumber && Boolean(errors.partNumber)}
                              endAdornment={
                                <InputAdornment position="end">
                                  {!readonly && isNew ? (
                                    <IconButton onClick={() => handleAddNewKit(0)}>
                                      <SearchIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )}
                                </InputAdornment>
                              }
                              autoFocus
                            />
                            <FormHelperText error={true}>{errors.partNumber && touched.partNumber ? errors.partNumber : null}</FormHelperText>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} lg={6}>
                          <FormControl fullWidth>
                            <InputLabel>Name</InputLabel>
                            <Input
                              disabled={true}
                              id="name"
                              value={values.name}
                              onChange={handleChange}
                              onClick={() => {
                                if (!readonly && isNew) {
                                  handleAddNewKit(1);
                                }
                              }}
                              error={touched.name && Boolean(errors.name)}
                              endAdornment={
                                <InputAdornment position="end">
                                  {!readonly && isNew ? (
                                    <IconButton onClick={() => handleAddNewKit(1)}>
                                      <SearchIcon />
                                    </IconButton>
                                  ) : (
                                    <></>
                                  )}
                                </InputAdornment>
                              }
                            />
                            <FormHelperText error={true}>{errors.name && touched.name ? errors.name : null}</FormHelperText>
                          </FormControl>
                        </Grid>

                        {values.partNumber !== "" ? (
                          <>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField disabled={true} id="platformVendorName" label="Platform Vendor" value={values.platformVendorName} onChange={handleChange} onBlur={handleBlur} error={touched.platformVendorKey && Boolean(errors.platformVendorKey)} helperText={errors.platformVendorKey && touched.platformVendorKey ? errors.platformVendorKey : null} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel id="platform-vendorI-id-label" shrink={true}>
                                  Ownership
                                </InputLabel>
                                <Select native labelId="device-ownership-id-label" id="deviceOwnershipKey" name="deviceOwnershipKey" value={values.deviceOwnershipKey} onChange={handleChange} label="Ownership">
                                  {referenceLookups.deviceOwnerships.map((deviceOwnership, index) => (
                                    <option value={deviceOwnership.deviceOwnershipKey} key={index}>
                                      {deviceOwnership.name}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{touched.deviceOwnershipKey && Boolean(errors.deviceOwnershipKey) ? errors.deviceOwnershipKey : ""}</FormHelperText>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={4} md={4} lg={6}>
                              <TextField disabled={readonly} id="quantity" label="Quantity" type="number" value={values.quantity} onChange={handleChange} onBlur={handleBlur} error={touched.quantity && Boolean(errors.quantity)} helperText={errors.quantity && touched.quantity ? errors.quantity : null} />
                            </Grid>

                            <Grid item xs={6} sm={4} md={4} lg={6}>
                                <FormControl disabled={readonly} className={globalStyles.formControl} fullWidth={true}>
                                <InputLabel id="platform-vendorI-id-label" shrink={true}>
                                  Rule
                                </InputLabel>
                                <Select native labelId="rule-id-label" id="packageKitRuleId" name="packageKitRuleId" value={values.packageKitRuleId} onChange={handleChange} label="Rule">
                                  {referenceLookups.kitRules.map((kitRules, index) => (
                                    <option value={kitRules.value} key={index}>
                                      {kitRules.text}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{touched.packageKitRuleId && Boolean(errors.packageKitRuleId) ? errors.packageKitRuleId : ""}</FormHelperText>
                              </FormControl>
                            </Grid>                            

                            {isSubscriptionBilling ? (
                              <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl className={globalStyles.formControl} fullWidth={true}>
                                  <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                    Rollup Item
                                  </FormLabel>
                                  <FormGroup>
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          disabled={readonly}
                                          id="rollupItem"
                                          name="rollupItem"
                                          checked={values.rollupItem}
                                          onChange={(ev, checked) => {
                                            setFieldValue("rollupItem", checked);
                                            if (checked) {
                                              getRechargeProductDetail(values.partNumber, checked);
                                            }
                                          }}
                                        />
                                      }
                                      label={values.rollupItem ? "Yes" : "No"}
                                      labelPlacement="end"
                                    />
                                  </FormGroup>
                                </FormControl>
                              </Grid>
                            ) : null}
                          </>
                        ) : null}

                        {values.rollupItem ? (
                          <Grid item xs={12}>
                            <Box className={globalStyles.topic}>Recharge Info</Box>
                          </Grid>
                        ) : null}
                        {rechargeProductSearchInProgress ? (
                          <Grid item xs={12}>
                            <Wait />
                          </Grid>
                        ) : null}

                        {values.rollupItem && !rechargeProductSearchInProgress ? (
                          <>
                            {values.shopifyVariantId === "" || values.shopifyVariantId === "0" ? (
                              <Grid item xs={12}>
                                <Box p={3}>
                                  <Alert severity="error">
                                    <Box p={1}>Could not find a Recharge Selling Plan for this SKU.</Box>
                                    <Box p={1}>This SKU was not found on the Shopify Customer facing site.</Box>
                                    <Box p={1}>Please correct this before making this a rollup item. </Box>
                                  </Alert>
                                </Box>
                              </Grid>
                            ) : (
                              <>
                                <Grid item xs={12} sm={12} md={9} lg={9}>
                                  <TextField disabled={true} id="title" label="Title" value={values.title} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <TextField disabled={true} id="intervalFrequency" label="Frequency" value={values.intervalFrequency} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <TextField disabled={true} id="intervalUnit" label="Unit" value={values.intervalUnit} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <TextField disabled={true} id="price" label="Price" value={values.price} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <TextField disabled={true} id="shopifyVariantId" label="Variant" value={values.shopifyVariantId} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <TextField disabled={true} id="subscriptionId" label="Subscription" value={values.subscriptionId} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <TextField disabled={true} id="taxable" label="Taxable" value={values.taxable ? "Yes" : "No"} />
                                </Grid>
                              </>
                            )}
                          </>
                        ) : null}

                        {!isNew ? (
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>Audit</Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField disabled={true} label="Last Modified By" value={initialFormValues.lastModifiedBy} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField disabled={true} label="Last Modified On" value={formatDateForDataGrid(initialFormValues.lastModifiedDateTime)} />
                            </Grid>
                          </Grid>
                        ) : null}

                        <Grid item xs={12}>
                          <Box p={3}></Box>
                        </Grid>
                        {values.partNumber !== "" && qrcodeBase64String !== "" ? (
                          <Grid container>
                            <Grid item xs={12}>
                              <Box p={0} display="flex" justifyContent="center">
                                <img src={qrcodeBase64String} style={{ width: "150px", height: "150px" }} alt="QR Code" />
                              </Box>
                              <Box p={0} display="flex" justifyContent="center">
                                <Link href={qrcodeBase64String} variant="body2" download="qrcode.png">
                                  <GetAppToneIcon fontSize="medium" color="secondary" />
                                </Link>
                              </Box>
                              <Box p={3}></Box>
                            </Grid>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </Box>
            </DialogContent>
            <DialogActions>
              {!readonly && !isNew ? (
                <Button onClick={() => openDeleteConfirm()} className={globalStyles.errorText}>
                  <DeleteIcon color="error" />
                </Button>
              ) : null}

              {!readonly ? (
                <Button disabled={!saveButtonVisible} onClick={() => formReference.current.handleSubmit()} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
                  {isNew ? "Save" : "Update"}
                </Button>
              ) : null}

              <Button onClick={() => cancelKitItem()} color="secondary" autoFocus className={globalStyles.buttonDefault}>
                <u>Cancel</u>
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      minHeight: "400px",
    },
  })
);

export default KitItemCRUD;
